import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import styled from "styled-components";
import {
  getConsultings,
} from "../../../api/consultings";
import { getMembers } from "../../../api/members";
import { formatDate } from "../../../util/Common";
import { MobilePagination } from "../../../components/mobile/MobilePagination";
import MobileSubTitle from "../../../components/mobile/MobileSubTitle";
import { consultingsState } from "../../../store/atom";
import ArrowIcon from "../../../components/icons/ArrowIcon";

const SubTitleContainer = styled.div`
  padding: 60px 16px 0;
  display: flex;
  align-items: flex-end;
  gap: 10px;
  color: #2aa4d5;
  font-weight: 700;

  h3 {
    font-size: 18px;
  }

  span {
    font-size: 20px;
  }

  ::before {
    position: absolute;
    left: 0px;
    bottom: calc(100% + 15px);
    content: "";
    display: block;
    width: 25px;
    height: 3px;
    background-color: #186fb2;
  }
`;

const Container = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0 16px;
  margin-bottom: 100px;
`;

const TableContainer = styled.section`
  width: 100%;
  height: 100%;
  font-size: 14px;
`;

const TableEl = styled.table`
  width: 100%;
  height: 100%;
  /* min-width: 400px; */
  border-top: 3px solid #2aa4d5;
  border-bottom: 2px solid #e5e5e5;
`;

const TableHead = styled.thead`
  background-color: #e9f6fb;
  font-weight: 700;
`;

const TableBody = styled.tbody`
  font-weight: 400;
  height: 100%;
  min-height: 400px;
`;

const TableRowEl = styled.tr`
  border-bottom: 1px solid #e5e5e5;

  &:last-child {
    border-bottom: none;
  }
`;

const TableBodyRow = styled(TableRowEl)`
  position: relative;
  &:hover {
    background-color: rgba(234, 234, 234, 0.7);
  }
`;

const TableHeadItem = styled.th`
  padding: 12px 6px;
`;

const TableHeadItemID = styled(TableHeadItem)`
  width: 60px;
  min-width: 50px;
`;

const TableHeadItemTitle = styled(TableHeadItem)`
  min-width: 100px;
`;
const TableHeadItemCompany = styled(TableHeadItem)`
  width: 150px;
  min-width: 100px;
`;

const TableHeadItemAuthor = styled(TableHeadItem)`
  width: 100px;
  min-width: 60px;
`;

const TableHeadItemCreatedAt = styled(TableHeadItem)`
  width: 140px;
  min-width: 140px;
`;

const TableBodyItem = styled.td`
  text-align: center;
  padding: 16px 6px;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const TableEmptyMessage = styled(TableRowEl)`
  justify-content: center;
`;

const TableBodyItemID = styled(TableBodyItem)`
  flex: 0.1;
  min-width: 50px;
`;

const TableBodyItemTitle = styled(TableBodyItem)`
  flex: 0.3;
  min-width: 120px;
  
`;
const TableBodyItemCompany = styled(TableBodyItem)`
  flex: 0.2;
  min-width: 100px;
`;

const TableBodyItemAuthor = styled(TableBodyItem)`
  width: 100px;
  min-width: 55px;
`;

const TableBodyItemCreatedAt = styled(TableBodyItem)`
  width: 180px;
  min-width: 180px;
  align-items: left;
`;

const DetailButton = styled.button`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 14px;
  cursor: pointer;
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);

  > svg {
    width: 100%;
    height: 100%;
  }
`;


export function Consulting() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [consultings, setConsultings] = useRecoilState(consultingsState);
  const [total, setTotal] = useState(0);
  const [size, setSize] = useState(8);
  const page = parseInt(searchParams.get("page") || "1");
  const fetchConsultings = useCallback(async () => {
    const data = await getConsultings(page, size);
    setConsultings(data.consultations);
    setTotal(data.total);
  }, [setConsultings, page, size]);
  const navigate = useNavigate();

  const handlePageChange = useCallback(
    (page) => {
      setSearchParams({ page: page.toString() });
    },
    [setSearchParams]
  );

  const calcItemsNumberStart = (page) => {
    return (page - 1) * size + 1;
  };

  const handleSizeChange = useCallback(
    (size) => {
      setSearchParams({ page: "1" });
      setSize(size);
    },
    [setSearchParams, setSize]
  );

  const handleDetailConsulting = (id) => {
    navigate(`/admin/consulting/${id}`);
  };

  useEffect(() => {
    fetchConsultings();
  }, [fetchConsultings]);



  useEffect(() => {
    const hasPage = searchParams.get("page");
    if (!hasPage) {
      navigate(`/admin/consulting?page=1`, { replace: true });
    }
  }, [searchParams, navigate]);

  if (page < 1) {
    return <Navigate to="/admin/consulting/1" replace />;
  }

  if (total !== 0 && page > Math.ceil(total / size)) {
    return <Navigate to={`/admin/consulting/${Math.ceil(total / size)}`} replace />;
  }

  return (
    <>
      <SubTitleContainer>
        <MobileSubTitle title="상담문의" />
      </SubTitleContainer>
      <Container>
        {/* <button onClick={() => setOpenCreateConsultingModal(true)}>
        상담문의 추가
      </button> */}
        <TableContainer>
          <TableEl>
            <TableHead>
              <TableRowEl>
                <TableHeadItemID>ID</TableHeadItemID>
                <TableHeadItemTitle>제목</TableHeadItemTitle>
                <TableHeadItemCompany>회사명</TableHeadItemCompany>
                <TableHeadItemAuthor>이름</TableHeadItemAuthor>
                <TableHeadItemCreatedAt>등록일</TableHeadItemCreatedAt>
              </TableRowEl>
            </TableHead>
            <TableBody>
              {consultings.length === 0 && (
                <TableEmptyMessage>
                  <p>상담문의 신청 내역이 없습니다.</p>
                </TableEmptyMessage>
              )}
              {
                consultings.map((consulting, index) => (
                  <ConsultingRow
                    key={consulting.id}
                    no={calcItemsNumberStart(page) + index}
                    {...consulting}
                    onClick={handleDetailConsulting}
                  />
                ))}
            </TableBody>
          </TableEl>
        </TableContainer>
        <MobilePagination
          totalItemsCount={total}
          activePage={parseInt(page)}
          itemsCountPerPage={size}
          pageRangeDisplayed={5}
          onChange={handlePageChange}
        />
      </Container>
    </>

  );
}

// function Pagination({ total, page, size, onChangePage, onChangeSize }) {
//   const totalPage = Math.ceil(total / size);

//   const getPageLinks = () => {
//     const pageLinks = [];
//     const startPage = Math.max(1, page - 2);
//     const endPage = Math.min(totalPage, startPage + 4);

//     for (let i = startPage; i <= endPage; i++) {
//       pageLinks.push(
//         <Link
//           key={i}
//           to={`/admin/consulting/${i}`}
//           style={{ fontWeight: i === page ? "bold" : "normal" }}
//         >
//           {i}
//         </Link>
//       );
//     }

//     return pageLinks;
//   };

//   return (
//     <PaginationContainer>
//       <PaginationFirst
//         onClick={(e) => {
//           e.preventDefault();
//           onChangePage(1);
//         }}
//         to={`#`}
//       >
//         &lt;
//       </PaginationFirst>
//       {getPageLinks().map((pageNum) => (
//         <PaginationButton
//           key={pageNum}
//           $isActive={pageNum === activePage}
//           onClick={(e) => {
//             e.preventDefault();
//             onChange(pageNum);
//           }}
//           to={`#`}
//         >
//           {pageNum}
//         </PaginationButton>
//       ))}
//       <PaginationLast
//         onClick={(e) => {
//           e.preventDefault();
//           onChange(totalPages);
//         }}
//         to={`#`}
//       >
//         &gt;
//       </PaginationLast>
//       <p>
//         {page} / {totalPage}
//       </p>
//       {getPageLinks()}
//       <select value={size} onChange={(e) => onChangeSize(e.target.value)}>
//         <option value={10}>10</option>
//         <option value={20}>20</option>
//         <option value={30}>30</option>
//       </select>
//     </PaginationContainer>
//   );
// }

function CreateConsultingModal({ onSave, onClose }) {
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");
  const formRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(formRef.current);
    const { ...consulting } = Object.fromEntries(formData);
    await onSave({
      memberId,
      ...consulting,
    });
  };
  const handleMemberChange = (e) => {
    setMemberId(e.target.value);
  };
  const fetchMembers = useCallback(async () => {
    const data = await getMembers();
    setMembers(data.members);
  }, [setMembers]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
      }}
    >
      <div
        style={{ width: "500px", height: "500px", backgroundColor: "white" }}
      >
        <h2>상담문의 추가</h2>
        <form ref={formRef} onSubmit={handleSubmit}>
          <input type="text" name="company" placeholder="회사명" required />
          <input type="text" name="author" placeholder="이름" required />
          <input type="tel" name="phone" placeholder="휴대폰 번호" />
          <input type="email" name="email" placeholder="이메일" />
          <select
            name="memberId"
            value={memberId}
            onChange={handleMemberChange}
            required
          >
            <option value="">담당자 선택</option>
            {members &&
              members.map((member) => (
                <option value={member.id} key={member.id}>
                  {member.name} {member.role}
                </option>
              ))}
          </select>
          <input
            type="text"
            name="howToReply"
            placeholder="답변 방법"
            required
          />
          <input type="text" name="title" placeholder="서비스명" required />
          <input type="text" name="content" placeholder="상담 내용" required />
          <button type="submit">추가</button>
          <button onClick={onClose}>닫기</button>
        </form>
      </div>
    </div>
  );
}


function ConsultingRow({ onClick, ...props }) {
  return (
    <TableBodyRow
      onClick={() => onClick(props.id)}
      style={{
        cursor: "pointer",
      }}
    >
      <TableBodyItemID>{props.id}</TableBodyItemID>
      <TableBodyItemTitle>{props.title}</TableBodyItemTitle>
      <TableBodyItemCompany>{props.company}</TableBodyItemCompany>
      <TableBodyItemAuthor>{props.author}</TableBodyItemAuthor>
      <TableBodyItemCreatedAt>{formatDate(props.createdAt)}
      </TableBodyItemCreatedAt>
      <DetailButton>
        <ArrowIcon fill="#262626" />
      </DetailButton>
    </TableBodyRow>
  );
};
